"use client";

import Script from "next/script";
import { useEffect } from "react";

import { useConsent } from "@/features/consent/useConsent";

import { applyGoogleAdsConsent } from "./consent";
import { googleAdsBootstrapSnippet } from "./gtag-snippet";

// Public Google Ads tag id (AW-…). Absent → the tag never loads (mirrors the
// TikTok pixel and PostHog token gates), so previews and local dev stay clean
// unless configured.
const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;

/**
 * Loads the Google Ads gtag.js tag using Consent Mode v2. Unlike the TikTok
 * pixel — which is not injected at all until consent is granted — gtag.js loads
 * on every page with the advertising signals defaulted to "denied", so it sets
 * no ad cookies before opt-in but can still model conversions. The bootstrap
 * snippet seeds the initial default from any persisted choice; when the visitor
 * grants (or later revokes) the `marketing` category the effect below pushes a
 * `consent: update`, flipping the signals live without a reload.
 */
export function GoogleAdsTag() {
  const { marketing, analytics, ready } = useConsent();

  useEffect(() => {
    if (!ready) {
      return;
    }
    applyGoogleAdsConsent(marketing ?? "denied", analytics ?? "denied");
  }, [marketing, analytics, ready]);

  if (!GOOGLE_ADS_ID) {
    return null;
  }

  return (
    <>
      <Script id="google-ads-consent" strategy="afterInteractive">
        {googleAdsBootstrapSnippet(GOOGLE_ADS_ID)}
      </Script>
      <Script
        id="google-ads-gtag"
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
        strategy="afterInteractive"
      />
    </>
  );
}
